/**
 * Which pages the app shows, and where that choice is stored.
 *
 * Deliberately **not** part of `feature-flags.ts`. Those are URL-only, dev-facing
 * and vanish on the next plain reload - that is the whole point of them. This is
 * the opposite: a persisted, user-facing choice about what the navigation
 * offers, set on the `/admin` page.
 *
 * **Scope: this browser.** It is `localStorage`, so it hides a page for whoever
 * set it, on the machine they set it on. It is not an org-wide setting and it is
 * not a permission - anyone can turn a page back on from `/admin`, and a hidden
 * page's URL still works if typed. Making it either of those needs the identity
 * and server-side storage described in `docs/plans/dashboards.md`.
 *
 * Every page names a `flag`, and the flag is the organisation's answer: it comes
 * from `cfg.feature_flag` through `GET /api/me`, can be targeted per user and
 * per group, and is enforced again on the API behind the page. A page needs
 * *both*: the flag on for this account, and this browser's setting not hiding
 * it. There is no permission that stands in for the flag any more: Features is
 * authoritative for administrators and Root too, and an administrator who
 * wants an in-development page switches it on for themselves there
 * (`docs/plans/access-management.md`).
 *
 * There used to be a third condition, `localOnly`, which hid a page wherever the
 * app was not served from a laptop. Dashboards was its only user and is now an
 * administrators-only flag instead (migration `0009`), which says the same thing
 * better: it can be changed without a deploy, it knows who is asking, and an
 * administrator can see and set it on `/admin` like every other feature.
 */

import { Atom, myAccess } from './store.ts';
import { hasFlag, type FeatureFlag } from './feature-flags.ts';

export const PAGE_VISIBILITY_KEY = 'neptune_page_visibility_v1';

/**
 * True when the browser is on `/admin`.
 *
 * A real path rather than a query param, because it is a different destination
 * rather than a different view of the app - and `frontend/Caddyfile` already
 * ends in `try_files {path} /index.html`, so the path resolves in production
 * without any server change.
 */
export function isAdminRoute(): boolean {
  if (typeof location === 'undefined') return false;
  return location.pathname.replace(/\/+$/, '') === '/admin';
}

export type PageId =
  | 'dashboards'
  | 'export'
  | 'plot'
  | 'modelviewer'
  | 'assistant'
  | 'argus'
  | 'live-status';

export interface PageInfo {
  id: PageId;
  label: string;
  description: string;
  /**
   * `section` pages are the header's Explore/Dashboards switch; `tab` pages are
   * the tabs inside Explore. The distinction matters for the "you cannot hide
   * the last one" rule, which applies to tabs only - Explore with no tabs has
   * nothing to render, while Dashboards is safe to hide entirely.
   */
  kind: 'section' | 'tab';
  /** The switch that governs this page, evaluated per account on the server. */
  flag?: FeatureFlag;
  /**
   * What to ask for when the page exists but not for this account - the hint
   * on its struck-through navigation entry (see `pageLock`).
   */
  request?: string;
}

export const PAGES: readonly PageInfo[] = [
  {
    id: 'dashboards',
    label: 'Dashboards',
    description: 'The multi-chart section: folders, dashboards and arranged tiles.',
    kind: 'section',
    flag: 'dashboards',
    request: 'Ask an administrator for an Explorer role.',
  },
  {
    id: 'plot',
    label: 'Plot',
    description: 'The single-chart workspace in Explore.',
    kind: 'tab',
    flag: 'plot',
  },
  {
    id: 'export',
    label: 'Export',
    description: 'Query, preview and download data as CSV, Parquet or Excel.',
    kind: 'tab',
    flag: 'export',
  },
  {
    id: 'modelviewer',
    label: 'Model Viewer',
    description: 'Service Power and SFOC model views.',
    kind: 'tab',
    flag: 'modelviewer',
  },
  {
    id: 'assistant',
    label: 'SONAR',
    description: 'SONAR, the in-development chat assistant, as a section and a drawer.',
    kind: 'section',
    flag: 'assistant',
    request: 'Ask an administrator for the AI Assistant User role.',
  },
  {
    id: 'argus',
    label: 'Argus',
    description: 'Deviation alerting: the mode selector and its rooms. See docs/plans/argus.md.',
    kind: 'section',
    flag: 'argus',
    request: 'Ask an administrator for an Argus role.',
  },
  {
    id: 'live-status',
    label: 'Live status',
    description: 'Whether live data arrives from every ship, per data stream.',
    kind: 'section',
    flag: 'livestatus',
    request: 'Ask an administrator for access to Live status.',
  },
];

type Settings = Partial<Record<PageId, boolean>>;

function read(): Settings {
  try {
    const raw = localStorage.getItem(PAGE_VISIBILITY_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    const out: Settings = {};
    for (const page of PAGES) {
      const v = (parsed as Settings)[page.id];
      if (typeof v === 'boolean') out[page.id] = v;
    }
    return out;
  } catch {
    return {};
  }
}

/**
 * Stored overrides only - an absent entry means "default", which is on. Storing
 * the absence rather than materialising every page keeps a future page visible
 * by default instead of hidden for everyone who ever opened `/admin`.
 */
export const pageVisibility = new Atom<Settings>(read());

/** Whether the page should appear in the navigation. */
export function isPageVisible(id: PageId): boolean {
  const page = PAGES.find((p) => p.id === id);
  if (!page) return false;
  if (page.flag && !hasFlag(page.flag)) return false;
  return pageVisibility.value[id] !== false;
}

/**
 * The request-access hint when a page exists but this account may not use it,
 * or null. Then the navigation shows the page struck through instead of
 * hiding it, so people know it is there and whom to ask.
 *
 * Only for a feature the server calls `no_access` - on for somebody, not for
 * this person (By role without the role, outside the audience, an exception).
 * A feature switched off for everyone (`off`) has nothing to request and stays
 * hidden, as does a page this browser's Pages setting hides. With no database
 * there is no answer (`myAccess` is null), and the page stays hidden.
 */
export function pageLock(id: PageId): string | null {
  const page = PAGES.find((p) => p.id === id);
  if (!page?.flag || hasFlag(page.flag) || pageVisibility.value[id] === false) return null;
  if (myAccess.value?.features[page.flag]?.state !== 'no_access') return null;
  return `You do not have access to ${page.label}. ${page.request ?? 'Ask an administrator to grant it.'}`;
}

/** Whether the setting is on, ignoring the flag gate. For the admin UI. */
export function isPageEnabled(id: PageId): boolean {
  return pageVisibility.value[id] !== false;
}

/** Tabs currently shown inside Explore, in the order the tab bar renders them. */
export function visibleTabs(): PageInfo[] {
  return PAGES.filter((p) => p.kind === 'tab' && isPageVisible(p.id));
}

/**
 * Hiding the last visible Explore tab would leave the section with nothing to
 * render, so it is refused. Dashboards has no such rule - the section switch
 * simply disappears.
 */
export function canDisable(id: PageId): boolean {
  const page = PAGES.find((p) => p.id === id);
  if (!page || page.kind !== 'tab') return true;
  return visibleTabs().length > 1;
}

export function setPageEnabled(id: PageId, enabled: boolean): void {
  if (!enabled && !canDisable(id)) return;
  const next: Settings = { ...pageVisibility.value };
  if (enabled) delete next[id];
  else next[id] = false;
  pageVisibility.set(next);
  try {
    localStorage.setItem(PAGE_VISIBILITY_KEY, JSON.stringify(next));
  } catch (e) {
    console.warn('Failed to persist page visibility', e);
  }
}

/** Turn everything back on - the way out of a configuration you regret. */
export function resetPageVisibility(): void {
  pageVisibility.set({});
  try {
    localStorage.removeItem(PAGE_VISIBILITY_KEY);
  } catch {
    /* ignore */
  }
}
